import type { CameraPose } from '@toolproof-core/visualization/_contract/camera';
import type { Vector3Tuple } from '@toolproof-core/visualization/_contract/geometry';

import type { Config } from '@/adapters/core/_contract/config';
import type { VisualNode } from '@/adapters/core/_pure/projection';

export type CoreLayoutEntry = {
    position: Vector3Tuple;
    angle: number;
    parentPath: string | null;
    visualRadius: number;
    subtreeRadius: number;
};

export type CoreLayout = {
    entryByPath: Map<string, CoreLayoutEntry>;
    ringCenterByPath: Map<string, Vector3Tuple>;
    ringRadiusByPath: Map<string, number>;
    boundsRadius: number;
};

/** Places the root at the origin and its children evenly on one XZ ring, widened only as needed for clearance. */
export function deriveRootRingLayout(args: {
    root: VisualNode;
    children: readonly VisualNode[];
    radius: number;
    rootRadius: number;
    childRadius: number;
    gap: number;
}): CoreLayout {
    const { root, children, radius, rootRadius, childRadius, gap } = args;
    const count = children.length;
    const ringRadius = Math.max(radius,
        ...(count > 0 ? [rootRadius + childRadius + gap] : []),
        ...(count > 1 ? [(2 * childRadius + gap) / (2 * Math.sin(Math.PI / count))] : []));
    const rootSubtreeRadius = count > 0 ? Math.max(rootRadius, ringRadius + childRadius) : rootRadius;
    const layout: CoreLayout = {
        entryByPath: new Map([[root.path, {
            position: [0, 0, 0], angle: 0, parentPath: null, visualRadius: rootRadius, subtreeRadius: rootSubtreeRadius,
        }]]),
        ringCenterByPath: new Map(count > 0 ? [[root.path, [0, 0, 0]]] : []),
        ringRadiusByPath: new Map([[root.path, ringRadius]]),
        boundsRadius: Math.max(radius, rootSubtreeRadius),
    };
    children.forEach((child, index) => {
        const angle = index / count * Math.PI * 2;
        layout.entryByPath.set(child.path, {
            position: [Math.cos(angle) * ringRadius, 0, Math.sin(angle) * ringRadius],
            angle, parentPath: root.path, visualRadius: childRadius, subtreeRadius: childRadius,
        });
    });
    return layout;
}

export function toOverviewCameraPose(boundsRadius: number, config: Config): CameraPose {
    const distance = Math.max(24, boundsRadius * config.geometry.cameraPadding);
    return { position: [0, distance * 0.78, distance * 0.92], target: [0, 0, 0], fov: 55 };
}
